// src/components/AddCompanyForm/index.jsx
import React, { useEffect, useRef, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Tooltip,
  Alert,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ActionBar from "./ActionBar";
import CompanyEssentialsCard from "./CompanyEssentialsCard";
import IdentityVisualCard from "./IdentityVisualCard";
import ContactCard from "./ContactCard";
import AddressLegalCard from "./AddressLegalCard";
import SocialsCard from "./SocialsCard";
import RegionsCard, { useRegionKeys } from "./RegionsCard";
import CepRangesCard from "./CepRangesCard";
import PermissionsCard from "./PermissionsCard";
import { useTheme } from "@mui/material/styles";
import { toHex, onlyDigits, slugify, isHttps, uid, validCep8, formatCepInput, parseCoord } from "./utils";
import { auditCepRanges, fingerprintCepRanges } from "./cepRangeAudit";
import { useAuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";

/** Container do formulário de empresas (estado + submit) */
export default function AddCompanyForm({ company, onClose, onSubmit, onSaved, isLoading }) {
  const theme = useTheme();
  const { userData } = useAuthContext();
  const { t } = useTranslation("settings");

  const [errorMessage, setErrorMessage] = useState(null);
  const [busyUpload, setBusyUpload] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Estado base
  const [form, setForm] = useState(() => ({
    id: company?.id ?? undefined,
    nome: company?.nome ?? "",
    codigo_interno: company?.codigo_interno ?? "",
    status: Number.isFinite(company?.status) ? Number(company.status) : 1,
    primary_color: toHex(company?.primary_color || ""),
    email: company?.email ?? "",
    telefone: company?.telefone ?? "",
    telefone_adicional: company?.telefone_adicional ?? "",
    support_email: company?.support_email ?? "",
    support_whatsapp: company?.support_whatsapp ?? "",
    site: company?.site ?? "",
    endereco: company?.endereco ?? "",
    cep: company?.cep ? formatCepInput(company.cep) : "",
    latitude: company?.latitude != null && company?.latitude !== "" ? String(company.latitude) : "",
    longitude: company?.longitude != null && company?.longitude !== "" ? String(company.longitude) : "",
    logo: company?.logo ?? null,
    _logoPreview: company?.logo ?? null,

    cnpj: company?.cnpj ?? "",
    atendimento_horario: company?.atendimento_horario ?? "",
    facebook: company?.facebook ?? "",
    instagram: company?.instagram ?? "",
    youtube: company?.youtube ?? "",
    linkedin: company?.linkedin ?? "",
  }));

  // Regiões
  const [regioes, setRegioes] = useState(() =>
    (Array.isArray(company?.regioes) ? company.regioes : []).map((r, idx) => ({
      _id: uid(),
      region_key: r.region_key,
      label: r.label,
      ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
      status: Number(r.status) === 0 ? 0 : 1,
      restrita: Number(r.restrita) === 1 ? 1 : 0,
      has_region_access_password: Boolean(r.has_region_access_password),
      nova_senha_acesso: "",
    }))
  );
  const regionKeys = useRegionKeys(regioes);

  // Faixas de CEP
  const [cepRanges, setCepRanges] = useState(() =>
    (Array.isArray(company?.cepRanges) ? company.cepRanges : []).map((r, idx) => ({
      _id: uid(),
      region_key: r.region_key,
      start_cep: r.start_cep || r.start || "",
      end_cep: r.end_cep || r.end || "",
      target_cod_interno: r.target_cod_interno || r.company || "",
      ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
      status: Number(r.status) === 0 ? 0 : 1,
    }))
  );
  const cepCardRef = useRef(null);
  const initialCepFpRef = useRef(fingerprintCepRanges(cepRanges));
  const [targetSlugsByRegion, setTargetSlugsByRegion] = useState(null);

  /** Usuários com acesso ao projeto — na criação, aplicado via PUT /companies/:id/acl após o POST. */
  const [draftAclUsers, setDraftAclUsers] = useState([]);

  /** Slug gerado do nome na inclusão; ao editar o slug manualmente, deixa de sobrescrever pelo nome. */
  const slugTouchedRef = useRef(false);
  const prevCompanyIdRef = useRef(company?.id);

  useEffect(() => {
    const prev = prevCompanyIdRef.current;
    prevCompanyIdRef.current = company?.id;
    if (prev != null && company?.id == null) slugTouchedRef.current = false;
  }, [company?.id]);

  useEffect(() => {
    if (form.id || slugTouchedRef.current) return;
    const next = slugify(form.nome);
    setForm((p) => (p.codigo_interno === next ? p : { ...p, codigo_interno: next }));
  }, [form.nome, form.id]);

  useEffect(() => {
    if (company?.id) return;
    setDraftAclUsers((prev) => {
      if (prev.length) return prev;
      if (!userData?.id) return [];
      return [
        {
          id: userData.id,
          nome: userData.nome || "",
          email: userData.email || "",
        },
      ];
    });
  }, [company?.id, userData?.id, userData?.nome, userData?.email]);

  // Atualiza quando abrir em modo edição (troca de empresa)
  useEffect(() => {
    setForm({
      id: company?.id ?? undefined,
      nome: company?.nome ?? "",
      codigo_interno: company?.codigo_interno ?? "",
      status: Number.isFinite(company?.status) ? Number(company.status) : 1,
      primary_color: toHex(company?.primary_color || ""),
      email: company?.email ?? "",
      telefone: company?.telefone ?? "",
      telefone_adicional: company?.telefone_adicional ?? "",
      support_email: company?.support_email ?? "",
      support_whatsapp: company?.support_whatsapp ?? "",
      site: company?.site ?? "",
      endereco: company?.endereco ?? "",
      cep: company?.cep ? formatCepInput(company.cep) : "",
      latitude: company?.latitude != null && company?.latitude !== "" ? String(company.latitude) : "",
      longitude: company?.longitude != null && company?.longitude !== "" ? String(company.longitude) : "",
      logo: company?.logo ?? null,
      _logoPreview: company?.logo ?? null,

      cnpj: company?.cnpj ?? "",
      atendimento_horario: company?.atendimento_horario ?? "",
      facebook: company?.facebook ?? "",
      instagram: company?.instagram ?? "",
      youtube: company?.youtube ?? "",
      linkedin: company?.linkedin ?? "",
    });

    const nextRegioes = Array.isArray(company?.regioes)
      ? company.regioes.map((r, idx) => ({
          _id: uid(),
          region_key: r.region_key,
          label: r.label,
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 0 ? 0 : 1,
          restrita: Number(r.restrita) === 1 ? 1 : 0,
          has_region_access_password: Boolean(r.has_region_access_password),
          nova_senha_acesso: "",
        }))
      : [];
    setRegioes(nextRegioes);

    const nextCepRanges = Array.isArray(company?.cepRanges)
      ? company.cepRanges.map((r, idx) => ({
          _id: uid(),
          region_key: r.region_key,
          start_cep: r.start_cep || r.start || "",
          end_cep: r.end_cep || r.end || "",
          target_cod_interno: r.target_cod_interno || r.company || "",
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 0 ? 0 : 1,
        }))
      : [];
    setCepRanges(nextCepRanges);
    initialCepFpRef.current = fingerprintCepRanges(nextCepRanges);
  }, [company?.id]); // eslint-disable-line

  const isEdit = Boolean(form.id);

  // Helpers
  const setFlash = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(null), 3000);
  };

  // Submit
  const save = async (e) => {
    e.preventDefault?.();
    setErrorMessage(null);

    if (!form.nome.trim()) return setFlash(t("addCompanyForm.flashNameRequired"));

    if (!form.id && draftAclUsers.length === 0) {
      return setFlash(t("addCompanyForm.flashAclUsersRequired"));
    }

    let projeto;
    let regioesOut;
    let rangesOut;

    if (!form.id) {
      const slugCreate = form.codigo_interno.trim();
      if (slugCreate && slugCreate !== slugify(slugCreate))
        return setFlash(t("addCompanyForm.flashSlugInvalid"));

      projeto = {
        nome: form.nome.trim(),
        status: Number(form.status) === 1 ? 1 : 0,
        codigo_interno: slugCreate || null,
        primary_color: null,
        logo: null,
        email: null,
        telefone: null,
        telefone_adicional: null,
        support_email: null,
        support_whatsapp: null,
        site: null,
        endereco: null,
        cep: null,
        latitude: null,
        longitude: null,
        cnpj: null,
        atendimento_horario: null,
        facebook: null,
        instagram: null,
        youtube: null,
        linkedin: null,
        id_usuario: company?.id_usuario ?? userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      };
      regioesOut = [];
      rangesOut = [];
    } else {
      const slug = form.codigo_interno.trim();
      if (slug && slug !== slugify(slug))
        return setFlash(t("addCompanyForm.flashSlugInvalid"));

      const color = toHex(form.primary_color);
      if (form.primary_color && !color)
        return setFlash(t("addCompanyForm.flashColorInvalid"));

      if (form.site && !isHttps(form.site))
        return setFlash(t("addCompanyForm.flashSiteHttps"));

      const socials = [
        ["facebook", form.facebook],
        ["instagram", form.instagram],
        ["youtube", form.youtube],
        ["linkedin", form.linkedin],
      ];
      for (const [key, val] of socials) {
        if (val && !isHttps(val))
          return setFlash(t("addCompanyForm.flashSocialUrl", { field: key.toUpperCase() }));
      }

      const cepDigits = onlyDigits(form.cep || "");
      if (form.cep?.trim() && !validCep8(cepDigits)) {
        return setFlash(t("addCompanyForm.address.flashCepInvalid"));
      }

      const lat = parseCoord(form.latitude);
      const lng = parseCoord(form.longitude);
      if (form.latitude?.trim() && !Number.isFinite(lat)) {
        return setFlash(t("addCompanyForm.address.flashLatitudeInvalid"));
      }
      if (form.longitude?.trim() && !Number.isFinite(lng)) {
        return setFlash(t("addCompanyForm.address.flashLongitudeInvalid"));
      }
      if (Number.isFinite(lat) && (lat < -90 || lat > 90)) {
        return setFlash(t("addCompanyForm.address.flashLatitudeInvalid"));
      }
      if (Number.isFinite(lng) && (lng < -180 || lng > 180)) {
        return setFlash(t("addCompanyForm.address.flashLongitudeInvalid"));
      }

      projeto = {
        id: form.id,
        nome: form.nome.trim(),
        codigo_interno: slug || null,
        status: Number(form.status) === 1 ? 1 : 0,
        primary_color: color || null,
        logo: form.logo || null,

        email: form.email.trim() || null,
        telefone: form.telefone.trim() || null,
        telefone_adicional: form.telefone_adicional.trim() || null,
        support_email: form.support_email.trim() || null,
        support_whatsapp: onlyDigits(form.support_whatsapp || "") || null,

        site: form.site.trim() || null,
        endereco: form.endereco.trim() || null,
        cep: cepDigits.length === 8 ? cepDigits : null,
        latitude: Number.isFinite(lat) ? lat : null,
        longitude: Number.isFinite(lng) ? lng : null,

        cnpj: onlyDigits(form.cnpj || "") || null,
        atendimento_horario: form.atendimento_horario.trim() || null,

        facebook: form.facebook.trim() || null,
        instagram: form.instagram.trim() || null,
        youtube: form.youtube.trim() || null,
        linkedin: form.linkedin.trim() || null,

        id_usuario: company?.id_usuario ?? userData?.id ?? null,
        id_usuario_ultima_alteracao: userData?.id ?? null,
      };

      regioesOut = regioes.map((r, idx) => {
        const base = {
          region_key: r.region_key,
          label: r.label,
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          status: Number(r.status) === 1 ? 1 : 0,
          restrita: Number(r.restrita) === 1 ? 1 : 0,
          _id: r._id,
        };
        const pw = String(r.nova_senha_acesso || "").trim();
        return pw ? { ...base, nova_senha_acesso: pw } : base;
      });

      const rangesDirty = fingerprintCepRanges(cepRanges) !== initialCepFpRef.current;
      if (rangesDirty) {
        const report =
          cepCardRef.current?.runLocalAudit?.() ||
          auditCepRanges(cepRanges, {
            regionKeys,
            targetSlugsByRegion: targetSlugsByRegion || undefined,
          });
        if (report.hasBlocking) {
          setFlash("Há faixas de CEP sobrepostas ou inválidas. Corrija antes de salvar as faixas.");
          cepCardRef.current?.openAudit?.();
          return;
        }
        rangesOut = cepRanges.map((r, idx) => ({
          region_key: String(r.region_key || "").trim(),
          start_cep: onlyDigits(r.start_cep),
          end_cep: onlyDigits(r.end_cep),
          target_cod_interno: slugify(r.target_cod_interno),
          status: Number(r.status) === 1 ? 1 : 0,
          ordem: Number.isFinite(+r.ordem) ? +r.ordem : idx,
          _id: r._id,
        }));
      }
    }

    try {
      const payload = { projeto, regioes: regioesOut };
      if (rangesOut !== undefined) payload.cepRanges = rangesOut;
      const res = await onSubmit(payload);
      let aclApplyFailed = false;
      if (!form.id) {
        const createdId = res?.data?.id;
        const users = draftAclUsers.map((u) => Number(u.id)).filter(Boolean);
        if (createdId && users.length) {
          try {
            await fetchData(`/companies/${createdId}/acl`, "PUT", { users });
          } catch {
            aclApplyFailed = true;
          }
        }
      }
      onSaved?.({ aclApplyFailed });
    } catch (e) {
      if (e?.status === 422 || e?.data?.hasBlocking) {
        cepCardRef.current?.openAudit?.();
      }
      /* erros de salvamento tratados no pai (mutate / snack) */
    }
  };

  const disabled = !form.nome.trim() || busyUpload;

  return (
    <Paper
      component="form"
      onSubmit={save}
      elevation={3}
      sx={{
        p: 2,
        pb: 0,
        width: "95%",
        flex: 1,
        minHeight: 0,
        m: "0 auto",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background:
          theme.palette.mode === "dark"
            ? "linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0.01))"
            : "linear-gradient(180deg, #fff, #fafafa)",
      }}
    >
      {/* Título */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 1, flexShrink: 0 }}>
        <Typography variant="h6">{isEdit ? t("addCompanyForm.titleEdit") : t("addCompanyForm.titleAdd")}</Typography>
        <Tooltip
          title={isEdit ? t("addCompanyForm.headerTooltip") : t("addCompanyForm.headerTooltipAdd")}
        >
          <InfoOutlinedIcon sx={{ ml: 1, fontSize: 18, color: "text.secondary" }} />
        </Tooltip>
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2, flexShrink: 0 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", pb: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <CompanyEssentialsCard
            form={form}
            setForm={setForm}
            isCreate={!isEdit}
            onSlugInput={
              !isEdit
                ? (value) => {
                    slugTouchedRef.current = true;
                    setForm((p) => ({ ...p, codigo_interno: value }));
                  }
                : undefined
            }
            onSlugBlur={
              !isEdit
                ? () =>
                    setForm((p) => ({
                      ...p,
                      codigo_interno: slugify(p.codigo_interno),
                    }))
                : undefined
            }
          />
        </Grid>

        <Grid item xs={12}>
          {isEdit ? (
            <PermissionsCard companyId={form.id} />
          ) : (
            <PermissionsCard draftUsers={draftAclUsers} onDraftUsersChange={setDraftAclUsers} />
          )}
        </Grid>

        {isEdit ? (
          <>
            <Grid item xs={12}>
              <IdentityVisualCard form={form} setForm={setForm} onBusyUpload={setBusyUpload} />
            </Grid>

            <Grid item xs={12} lg={6}>
              <ContactCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12} lg={6}>
              <AddressLegalCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12}>
              <SocialsCard form={form} setForm={setForm} />
            </Grid>

            <Grid item xs={12}>
              <Accordion expanded={advancedOpen} onChange={(_, exp) => setAdvancedOpen(exp)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="company-advanced-content">
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", textAlign: "left" }}>
                    <Typography fontWeight={600}>{t("addCompanyForm.advanced.title")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t("addCompanyForm.advanced.summary")}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <RegionsCard regioes={regioes} setRegioes={setRegioes} />
                    </Grid>
                    <Grid item xs={12}>
                      <CepRangesCard
                        ref={cepCardRef}
                        companyId={form.id}
                        regionKeys={regionKeys}
                        cepRanges={cepRanges}
                        setCepRanges={setCepRanges}
                        targetSlugsByRegion={targetSlugsByRegion}
                        onTargetSlugsByRegion={setTargetSlugsByRegion}
                      />
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </>
        ) : null}
      </Grid>
      </Box>

      <ActionBar onClose={onClose} isFetching={isLoading} disabled={disabled}>
        {busyUpload ? t("addCompanyForm.uploadingLogo") : null}
      </ActionBar>
    </Paper>
  );
}
